import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import type { Camera } from './Camera';
import { FrameBuffer } from './FrameBuffer';
import { FullscreenQuad } from './FullscreenQuad';
import { BloomPass } from './BloomPass';
import { ShaderProgram } from './ShaderProgram';

import fullscreenVert from '../shaders/fullscreen.vert.glsl?raw';
import compositeFrag from '../shaders/bloom-composite.frag.glsl?raw';

export interface Renderable {
  render(gl: WebGL2RenderingContext, camera: Camera): void;
}

const BLOOM_STRENGTH = 0.5;

/**
 * HDR render pipeline: scene → FBO → bloom → tonemap → screen.
 * Falls back to direct rendering if EXT_color_buffer_float is unavailable.
 */
export class RenderPipeline implements GameModule {
  readonly name = 'RenderPipeline';
  drawCallCount = 0;

  private _gl!: WebGL2RenderingContext;
  private _camera!: Camera;
  private _renderables: Renderable[] = [];

  private _hdrSupported = false;
  private _hdrFbo!: FrameBuffer;
  private _bloomPass!: BloomPass;
  private _quad!: FullscreenQuad;
  private _compositeShader!: ShaderProgram;
  private _canvasWidth = 0;
  private _canvasHeight = 0;

  constructor(camera: Camera) {
    this._camera = camera;
  }

  init(engine: Engine): void {
    this._gl = engine.gl;
    const gl = this._gl;

    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.clearColor(0.0, 0.0, 0.0, 1.0);

    this._hdrSupported = !!gl.getExtension('EXT_color_buffer_float');

    if (this._hdrSupported) {
      this._canvasWidth = engine.canvas.width;
      this._canvasHeight = engine.canvas.height;
      this._quad = new FullscreenQuad(gl);
      this._hdrFbo = new FrameBuffer(
        gl, this._canvasWidth, this._canvasHeight,
        gl.RGBA16F, gl.LINEAR, true,
      );
      this._bloomPass = new BloomPass(
        gl, this._quad, this._canvasWidth, this._canvasHeight,
      );
      this._compositeShader = new ShaderProgram(gl, fullscreenVert, compositeFrag);
    }

    engine.events.on<{ width: number; height: number }>('engine:resize', ({ width, height }) => {
      if (!this._hdrSupported) return;
      this._canvasWidth = width;
      this._canvasHeight = height;
      this._hdrFbo.resize(width, height);
      this._bloomPass.resize(width, height);
    });
  }

  addRenderable(r: Renderable): void {
    this._renderables.push(r);
  }

  removeRenderable(r: Renderable): void {
    const i = this._renderables.indexOf(r);
    if (i >= 0) this._renderables.splice(i, 1);
  }

  update(_dt: number): void {
    const gl = this._gl;
    this.drawCallCount = 0;

    if (this._hdrSupported) {
      this._renderHDR(gl);
    } else {
      this._renderLDR(gl);
    }
  }

  dispose(): void {
    this._renderables.length = 0;
    if (this._hdrSupported) {
      this._hdrFbo.dispose();
      this._bloomPass.dispose();
      this._quad.dispose();
      this._compositeShader.dispose();
    }
  }

  private _renderHDR(gl: WebGL2RenderingContext): void {
    this._hdrFbo.bind();
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    for (let i = 0; i < this._renderables.length; i++) {
      this._renderables[i].render(gl, this._camera);
      this.drawCallCount++;
    }
    this._hdrFbo.unbind();

    this._bloomPass.render(this._hdrFbo.texture);
    this.drawCallCount += 8;

    gl.viewport(0, 0, this._canvasWidth, this._canvasHeight);
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.BLEND);

    this._compositeShader.use();
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this._hdrFbo.texture);
    this._compositeShader.setInt('u_scene', 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this._bloomPass.resultTexture);
    this._compositeShader.setInt('u_bloom', 1);
    this._compositeShader.setFloat('u_bloomStrength', BLOOM_STRENGTH);
    this._quad.draw();
    this.drawCallCount++;

    gl.enable(gl.DEPTH_TEST);
    gl.enable(gl.BLEND);
  }

  private _renderLDR(gl: WebGL2RenderingContext): void {
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    for (let i = 0; i < this._renderables.length; i++) {
      this._renderables[i].render(gl, this._camera);
      this.drawCallCount++;
    }
  }
}
